import { useCallback } from 'react';

import { graphql, useFragment, useMutation } from 'react-relay';

import type { NetworkStorageParams } from '../../../../common/types';

import type { useNavSettingsService$key } from './__generated__/useNavSettingsService.graphql';
import type {
	useNavSettingsServiceMutation,
	useNavSettingsServiceMutation$variables,
} from './__generated__/useNavSettingsServiceMutation.graphql';

export type UseNavSettingsServiceParams = Omit<NetworkStorageParams, 'entityAri' | 'queryRef'> & {
	mutationEntityAri: string;
	queryRef: useNavSettingsService$key | null | undefined;
};

export function useNavSettingsService(params: UseNavSettingsServiceParams) {
	const { mutationEntityAri: entityAri, ownerAri, queryRef } = params;
	const queryData = useLocalFragment(queryRef);
	const mutation = useLocalMutation();

	// Remove when navx-noissue-20250918-fix-types is cleaned up
	const setNavDataAsync = useCallback(
		async ({
			sidebar,
			properties,
		}: Pick<useNavSettingsServiceMutation$variables, 'sidebar' | 'properties'>) => {
			return executeMutation({ mutation, variables: { entityAri, ownerAri, properties, sidebar } });
		},
		[entityAri, mutation, ownerAri],
	);

	// Remove `Sync` suffix when navx-noissue-20250918-fix-types is cleaned up
	const setNavDataSync = useCallback(
		({
			sidebar,
			properties,
		}: Pick<useNavSettingsServiceMutation$variables, 'sidebar' | 'properties'>) => {
			executeMutation({ mutation, variables: { entityAri, ownerAri, properties, sidebar } });
		},
		[entityAri, mutation, ownerAri],
	);

	return { queryData, setNavDataAsync, setNavDataSync };
}

function useLocalFragment(queryRef: UseNavSettingsServiceParams['queryRef']) {
	/* eslint-disable @atlassian/relay/no-wrapping-hooks */
	/* eslint-disable @atlassian/relay/unused-fields */
	/* eslint-disable @atlassian/relay/graphql-naming */
	return useFragment<useNavSettingsService$key>(
		graphql`
			fragment useNavSettingsService on SettingsNavigationCustomisation {
				sidebar {
					nodes {
						menuId
						visible
					}
				}
				properties {
					nodes {
						key
						value
					}
				}
			}
		`,
		queryRef,
	);
	/* eslint-enable @atlassian/relay/unused-fields */
}

function useLocalMutation() {
	return useMutation<useNavSettingsServiceMutation>(graphql`
		mutation useNavSettingsServiceMutation(
			$entityAri: ID!
			$ownerAri: ID!
			$sidebar: [SettingsMenuItemInput]
			$properties: [SettingsDisplayPropertyInput]
		) {
			settings_updateNavigationCustomisation(
				input: {
					entityAri: $entityAri
					ownerAri: $ownerAri
					properties: $properties
					sidebar: $sidebar
				}
			) {
				sidebar {
					nodes {
						menuId
						visible
					}
				}
				properties {
					nodes {
						key
						value
					}
				}
			}
		}
	`)[0];
}

type ExecuteMutationParams = {
	mutation: ReturnType<typeof useLocalMutation>;
	variables: useNavSettingsServiceMutation$variables;
};

function executeMutation({ mutation, variables }: ExecuteMutationParams) {
	try {
		return mutation({
			variables: variables,
			onCompleted: () => {
				// TODO: fire analytic event for success after NAVX-490 is merged
			},
			onError: (/*error*/) => {
				// TODO: fire analytic event for success after NAVX-490 is merged
			},
		});
	} catch {}
}
